import React, {useState, useRef, useEffect} from 'react';

import {useGlobalState, isMobile, jsonToFormData, FiltersConfigParam} from '../state';
import ReactHtmlParser, {convertNodeToElement} from "react-html-parser";
import InfiniteScroll from 'react-infinite-scroller';
import {useInfiniteQuery, useQueryClient} from "@tanstack/react-query";
import useLocalStorage from "use-local-storage";

export default function ProductsList(props) {
    const [inovaCheckedIds, setInovaCheckedIds] = useState([]);
    const [isFirstLoadFinished, setIsFirstLoadFinished] = useGlobalState('firstLoadFinished');
    const [hasMore, setHasMore] = useState(true);

    const [page] = useGlobalState('page');
    const [isBYO] = useGlobalState('isBYO');
    const [type] = useGlobalState('type');
    const [subtype] = useGlobalState('subtype');
    const [sortBy] = useGlobalState('sortBy');
    const [sortDirection] = useGlobalState('sortDirection');
    const [dShape] = useGlobalState('dShape');
    const [dType] = useGlobalState('dType');
    const [BYOSelectedDiamondID] = useGlobalState('BYOSelectedDiamondID');
    const [searchQuery] = useGlobalState('searchQuery');
    const [filtersConfig, setFiltersConfig] = useGlobalState('filters');
    const [randomSeed, setRandomSeed] = useLocalStorage('productsRandomSeed', Math.floor(Math.random() * 999));
    const [isFromDetailViewNavigation, setIsFromDetailViewNavigation] = useLocalStorage('isFromDetailViewNavigation', 'false');

    const scrollRef = useRef(null);
    const productsRef = useRef(null);

    useEffect(() => {
        if(isFromDetailViewNavigation === 'false') {
            scrollRef.current.pageLoaded = 0;
            productsRef.current.scrollTo(0, 0);
            window.localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE');
        }
        else {
            setIsFromDetailViewNavigation('false');
        }
    }, [])

    /** fetch new products */
    const productListResult = useInfiniteQuery(['products', sortBy, sortDirection, searchQuery, filtersConfig], async ({ signal, pageParam = 0 }) => {
        if(pageParam == null) pageParam = 0;
        scrollRef.current.pageLoaded = pageParam;
        return await fetchDisplayBlockImpl(signal, pageParam !== 0, pageParam);
    }, {
        // refetchOnMount: true,
        getNextPageParam: (lastPage, pages) => {
            return lastPage ? lastPage.pageNumber + 1 : 0;
        },
        getPreviousPageParam: (lastPage, pages) => {
            if(!lastPage) return 0;
            return lastPage.pageNumber - 1 < 0 ? 0 : lastPage.pageNumber - 1;
        },
    })

    return (
        <InfiniteScroll
            ref={scrollRef}
            initialLoad={false}
            pageStart={0}
            threshold={isMobile() ? 1500 : 600}
            loadMore={(page) => {
                if(!productListResult.isFetchingNextPage && !productListResult.isFetching) {
                    productListResult.fetchNextPage()
                }
            }}
            hasMore={hasMore}
            loader={productListResult.hasNextPage ? <div style={{display: 'flex', justifyContent: "center"}}><div className="loader"/></div> : null}
        >
            <div className="container">
                <div id="products" className="row list-group" ref={productsRef}>
                    {!productListResult.data &&
                        <div style={{display: 'flex', justifyContent: "center"}}><div className="loader"/></div>
                    }
                    {productListResult.data && productListResult.data.pages && productListResult.data.pages.map((page, i) => (
                        <React.Fragment key={i}>
                            {ReactHtmlParser(page.displayBlock, {
                                transform: (node) => transformNode(node)
                            })}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </InfiniteScroll>
    )

    function transformNode(node, isChild = false) {
        if(node.attribs && node.attribs.class && node.attribs.class.includes("item")) {
            return <div {...node.attribs} style={{}} onClick={() => setIsFromDetailViewNavigation('true')}>
                {node.children.map(child => transformNode(child, true))}
            </div>
        }
        //image of product
        if(node.type === 'tag' && node.name === 'img' && node.attribs.class && node.attribs.class.includes('product-img')) {
            return <img {...node.attribs} style={{opacity: 0, transition: "opacity 0.3s ease-in", aspectRatio: node.attribs.src.includes('ZURI') ? null : "1", width: "100%"}} onLoad={e => e.currentTarget.style.opacity = '1'} onMouseEnter={e => switchImage(e, node.attribs['image2'])} onMouseLeave={e => switchImage(e, node.attribs['image1'])}/>
        }
        //variant button
        if(node.type === 'tag' && node.name === 'span' && node.attribs.class && node.attribs.class.includes('variant-btn')) {
            return <span id={node.attribs.id} style={{cursor: "pointer"}} onClick={(e) => switchVariant(e, JSON.parse(node.attribs['variant-config']))}><i {...node.children[0].attribs}></i></span>
        }

        if(isChild) {
            const el = convertNodeToElement(node);
            if(node.children && !(node.name === 'img' || node.name === 'input')) {
                el.props.children = node.children.map((child) => transformNode(child, true))
            }
            return el;
        }
    }

    async function fetchDisplayBlockImpl(abortSignal, isNextPageRequest = false, pageNumber = 0) {
        if(!isNextPageRequest) {
            setInovaCheckedIds([]);
            setHasMore(true);
        }

        const res = await fetch(page === 'search' ? '/search/main.php' : (isBYO ? '/build_your_own/main.php' : '/products_list/main.php'), {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" },
            signal: abortSignal,
            body: new URLSearchParams(jsonToFormData({
                isUpdate: true,
                page: page,
                type: type,
                subtype: subtype,
                pageNumber: pageNumber,
                randomSeed: randomSeed,
                sortBy: sortBy,
                sortDirection: sortDirection,
                filters: FiltersConfigParam.encode(filtersConfig),
                step: isBYO ? 'setting' : '',
                dshape: dShape,
                dtype: dType,
                sdid: BYOSelectedDiamondID,
                search: searchQuery
            }))
        });
        const data = await res.json();
        let _displayBlock = "";
        if(!isNextPageRequest) {
            if(data.html === "") {
                if(type && type.length > 0) {
                    var typePlural = type === 'earring' ? 'earrings': type;
                    _displayBlock = '<div id="no-diamonds-found" style="color: black; width: 100%; text-align: center; padding: 16px 0; font-size: 16px;">' +
                        'Currently, no '+type+'s on our site meet your criteria.<br>' +
                        'We are continually adding '+type+'s to our inventory. Try to expand some of your specifications, or you can also call us at <a href="tel:18883911130">1-888-391-1130</a>.<br>' +
                        'If we have it in our stock, we will share images and details.<br>' +
                        'We can also custom make it for you.<br>' +
                        'We promise to make the '+typePlural+' you are looking for at a lower price than any other Online jewelers.<br>' +
                        'Please check our custom made jewelry process.' +
                        '</div>'
                } else {
                    _displayBlock = '<div id="no-diamonds-found" style="color: black; width: 100%; text-align: center; padding: 16px 0; font-size: 16px;">' +
                        'No items were found that meet your criteria. Try to expand some of your specifications.<br> ' +
                        'You can also call us at <a href="tel:18883911130">1-888-391-1130</a>. We promise to find the items you are looking for at a lower price than any other online jeweler.' +
                        '</div>';
                }
            } else {
                _displayBlock = data.html;
                addInovaARButtons();
            }

            await setFiltersConfig(data.filters);

            if(!isFirstLoadFinished) {
                await setIsFirstLoadFinished(true);
            }
        } else {
            if(data.html !== "") {
                _displayBlock = data.html;
                addInovaARButtons();
            }
        }
        if (data.randomSeed) setRandomSeed(data.randomSeed);
        setHasMore(data.html !== "");
        return {
            pageNumber: pageNumber,
            displayBlock: _displayBlock
        };
    }

    async function addInovaARButtons() {
        const checkedIds = inovaCheckedIds;
        const ids = [...productsRef.current.querySelectorAll('div')].map(el => el.getAttribute('inova-id')).filter(id => id != null && !checkedIds.includes(id));

        checkedIds.push(...ids);

        if(productsRef.current.querySelector('#WA_Paas_iFrame_Check')) {
            productsRef.current.querySelector('#WA_Paas_iFrame_Check').style.display = 'none';
        }

        //var user_ring = "11355593^metall:w,11356984^metall:w,11355601^metall:w,11356976^metall:w,11355619^metall:w,11355608^metall:w,11355588^metall:w,11356995^metall:w,11356976^metall:w";
        var user_ring = checkedIds.join(",");
        const res = await fetch("https://demo4.paasweb.co.il/-ring_designer?d=662&CheckRingExists=2&multiCheck=1&user_ring=" + user_ring);
        const data = await res.text();

        const result = data.split(",");
        for(const id of result) {
            if (id.substr(-1) === '1'){
                var temp = id.slice(0, -2).split('-');
                var ARId = temp[0] + "^metall:" + temp[1];
                const arimg = document.createElement('img');
                arimg.src = 'https://demo4.paasweb.co.il/Include/Inova/Images/Buttons/en/hand_1.png';
                arimg.classList.add('smallImgTmb');
                arimg.setAttribute("style", "right: 20px; left: initial;");
                requestAnimationFrame(() => {
                    if(productsRef.current.querySelector('div[inova-id="' + ARId + '"]')) {
                        productsRef.current.querySelector('div[inova-id="' + ARId + '"]').append(arimg);
                    }
                })
            }
        }
        setInovaCheckedIds(checkedIds);
    }

    function switchImage(event, image) {
        event.currentTarget.setAttribute('src', image);
    }

    function switchVariant(event, variantConfig) {
        const el = event.currentTarget.parentNode.parentNode.parentNode.parentNode;
        const link = variantConfig.link;
        // const link = variantConfig.isBYO ?
        //     '/build_your_own.php?step=settingdetail&type='+variantConfig.type+'&sdid='+variantConfig.BYOSelectedDiamondID+'&stid='+variantConfig.style_id+'&m='+variantConfig.letter_code+'&ct='+variantConfig.carat+'&dshape=' + variantConfig.BYOSelectedDiamondShape + '&dtype='+dType+ '&dpairid=&item_id=' + variantConfig.item_price_id :
        //     '/product-detail-view.php?type='+variantConfig.type+'&sid='+variantConfig.style_id+'&m='+variantConfig.letter_code+'&ct='+variantConfig.carat+'&new=true';
        el.querySelector('#detail_view_link').setAttribute('href', link);
        el.querySelector('#detail_view_link_img').setAttribute('href', link);
        el.querySelector('img').setAttribute('src', variantConfig.image1);
        el.querySelector('img').removeEventListener('mouseenter', switchImage, false);
        el.querySelector('img').removeEventListener('mouseleave', switchImage, false);
        el.querySelector('img').addEventListener('mouseenter', function(event) { switchImage(event, variantConfig.image2); });
        el.querySelector('img').addEventListener('mouseleave', function(event) { switchImage(event, variantConfig.image1); });
        el.querySelector('h5').innerHTML = variantConfig.description;

        if(variantConfig.discounted_price) {
            el.querySelector('#variant-price').innerText = '$' + variantConfig.discounted_price;
            el.querySelector('#original-price').innerText = '$' + variantConfig.retail_price + " ";
        }
        else {
            el.querySelector('#variant-price').innerText = '$' + variantConfig.retail_price;
        }
    }
}
